/**
 * SPEC-ADMIN2 — derive the summary stats shown on the admin user profile
 * ("künye") card from the already-loaded engagement list.
 *
 * `engagements === null` means the panel is still loading, so every derived
 * value is `null` (the card distinguishes this from a genuine 0). Once the
 * list resolves the counts are real numbers, even when empty.
 */
import type { EngagementHistoryEntry } from "@/types";

export interface UserStats {
  engagementCount: number | null;
  totalContracts: number | null;
  /** Original ISO string of the most-recent activity, or null. */
  lastActivity: string | null;
}

export function deriveUserStats(
  engagements: EngagementHistoryEntry[] | null,
): UserStats {
  if (engagements === null) {
    return { engagementCount: null, totalContracts: null, lastActivity: null };
  }

  const engagementCount = engagements.length;
  const totalContracts = engagements.reduce(
    (sum, e) => sum + (e.contractCount ?? 0),
    0,
  );

  let lastActivity: string | null = null;
  let lastActivityMs = -Infinity;
  for (const e of engagements) {
    const iso = e.lastResult?.calculatedAt ?? e.updatedAt;
    const ms = new Date(iso).getTime();
    if (!Number.isNaN(ms) && ms > lastActivityMs) {
      lastActivityMs = ms;
      lastActivity = iso;
    }
  }

  return { engagementCount, totalContracts, lastActivity };
}
